import { format, parseISO } from 'date-fns';

export const formatCurrency = (amount, currency = 'INR') => {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
};

/** Show transaction currency with INR equivalent when different (Beyond Square base). */
export const formatCurrencyDual = (amount, currency = 'INR', conversionRate = null) => {
  if (amount == null) return '—';
  const primary = formatCurrency(amount, currency);
  if (!currency || currency === 'INR' || !conversionRate) return primary;
  const inr = parseFloat(amount) * parseFloat(conversionRate);
  return `${primary} (${formatCurrency(inr, 'INR')})`;
};

export const formatDate = (date) => {
  if (!date) return '—';
  try { return format(typeof date === 'string' ? parseISO(date) : date, 'dd MMM yyyy'); } catch { return date; }
};

export const formatDateTime = (date) => {
  if (!date) return '—';
  try { return format(typeof date === 'string' ? parseISO(date) : date, 'dd MMM yyyy, HH:mm'); } catch { return date; }
};

export const formatPercent = (v) => v != null ? `${parseFloat(v).toFixed(2)}%` : '—';

/** PTE UI/accounting — taxable only. O2C/Tally gross lives in credit_notes.amount. */
export function creditNoteTaxableAmount(note) {
  if (note?.credited_taxable_value != null && note.credited_taxable_value !== '') {
    return Math.abs(parseFloat(note.credited_taxable_value));
  }
  return Math.abs(parseFloat(note?.amount || 0));
}

/** Invoice taxable base for PTE balances (subtotal, not gross). */
export function invoiceTaxableAmount(invoice) {
  const subtotal = parseFloat(invoice?.subtotal);
  if (Number.isFinite(subtotal) && subtotal >= 0) return subtotal;
  return parseFloat(invoice?.total_amount || 0);
}

/** SO mod disposition / CN rows — taxable only in PTE UI. */
export function dispositionTaxableAmount(row) {
  const calc = row?.credit_calculation;
  if (calc?.credited_taxable_value != null && calc.credited_taxable_value !== '') {
    return Math.abs(parseFloat(calc.credited_taxable_value));
  }
  if (calc?.original_taxable != null && calc?.corrected_taxable != null) {
    return Math.abs(parseFloat(calc.original_taxable) - parseFloat(calc.corrected_taxable));
  }
  return Math.abs(parseFloat(row?.amount ?? 0));
}

const STATUS_COLOR = {
  // Green
  PAID: 'green', ACTIVE: 'green', COMPLETED: 'green', APPROVED: 'green',
  ACCEPTED: 'green', POSTED: 'green', SENT: 'green', FULLY_RECOGNISED: 'green',
  FULLY_INVOICED: 'green', CONFIRMED: 'green', CONNECTED: 'green',
  // Orange (BT primary)
  PENDING: 'orange', PENDING_APPROVAL: 'orange', DUE: 'orange',
  OPEN: 'orange', DRAFT: 'orange', SUBMITTED: 'orange', TRIGGERED: 'orange',
  PARTIALLY_PAID: 'orange', PARTIALLY_RECOGNISED: 'orange',
  // Red
  OVERDUE: 'red', CANCELLED: 'red', REJECTED: 'red', REVERSED: 'red',
  DISPUTED: 'red', FAILED: 'red', EXPIRED: 'red', CLOSED: 'red',
  // Blue
  VIEWED: 'blue', PENDING_PAYMENT: 'blue', AUTO_APPROVE: 'blue',
  // Amber
  REVISION_REQUESTED: 'amber', WAIVED: 'amber', SUSPENDED: 'amber',
  AMENDED: 'amber', DRAFT_FOR_REVIEW: 'amber',
};

export const StatusBadge = ({ status }) => {
  if (!status) return null;
  const colour = STATUS_COLOR[status] || 'gray';
  return (
    <span className={`bt-badge bt-badge-${colour}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
};

export const TERM_TYPES = [
  { value: 'DELIVERY_BASED',  label: 'Delivery Based' },
  { value: 'PERIOD_TRANCHE',  label: 'Period Tranche' },
  { value: 'SUBSCRIPTION',    label: 'Subscription' },
  { value: 'MILESTONE_BASED', label: 'Milestone Based' },
  { value: 'ADVANCE_BASED',   label: 'Advance Based' },
];

export const TRIGGER_TYPES = [
  { value: 'PO_CONFIRMATION', label: 'SO Confirmation' },
  { value: 'DELIVERY_ACCEPTANCE', label: 'Delivery Acceptance' },
  { value: 'MILESTONE_ACCEPTANCE', label: 'Milestone Acceptance' },
  { value: 'TIME_PERIOD', label: 'Time Period' },
  { value: 'SUBSCRIPTION_CYCLE', label: 'Subscription Cycle' },
  { value: 'MANUAL', label: 'Manual' },
];

export const ANCHOR_TYPES = [
  { value: 'PO_CONFIRM_DATE', label: 'SO Confirm Date' },
  { value: 'DELIVERY_DATE', label: 'Delivery Date' },
  { value: 'MILESTONE_DATE', label: 'Milestone Date' },
  { value: 'PREVIOUS_INSTALLMENT_DATE', label: 'Previous Installment Date' },
  { value: 'SUBSCRIPTION_START_DATE', label: 'Subscription Start Date' },
];

export function getTermTypeLabel(type) {
  const map = {
    A: '100% on Delivery', B: 'Advance + Delivery', C: 'Advance + Delivery + Retention',
    D: '100% Deferred', E: 'Milestone Split', F: 'Time Tranche',
    G: 'Auto-Renew Subscription', H: 'Approval-Renew Subscription', I: 'Multi-Year Fixed'
  };
  return map[type] || type;
}

export function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}
